// Adapter: the rich diligenciaDisplay model (SOT for how a diligencia stage
// reads) → the "Diligencia activa" stagepill the Causas table draws. Derived
// from the served `tasks.stage` + deadline + quote tallies.
import { diligenciaDisplay, formatDeadline } from './diligenciaDisplay';

// Operational stage ladder — the 5 buckets a diligencia moves through, in order.
// `step` orders the "Diligencia activa" sort; `label` is the stagepill text.
export const STAGE_DEFS = [
  { key: 'borrador', step: 0, label: 'Borrador' },
  { key: 'cotizando', step: 1, label: 'Cotizando' },
  { key: 'porpagar', step: 2, label: 'Por pagar' },
  { key: 'encurso', step: 3, label: 'En curso' },
  // Owner naming: the terminal bucket reads "Finalizada" (not "Cerrada");
  // cancelled/expired rows override the label with their own reading below.
  { key: 'cerrada', step: 4, label: 'Finalizada' },
];

// Stagepill tone per bucket. Late/expired diligencias override these with
// 'warn' / 'err'.
export const STAGE_TONE = {
  borrador: 'mute',
  cotizando: 'info',
  porpagar: 'ready',
  encurso: 'live',
  cerrada: 'ok',
  desconocido: 'mute',
};

const STAGE_BY_KEY = Object.fromEntries(STAGE_DEFS.map((s) => [s.key, s]));

// tasks.stage → bucket. The three payment stages share the actionable
// "Por pagar" bucket: due, in flight and rejected are one column of work for
// the operator, distinguished by the estado reading. CANCELLED is terminal:
// a cancelled diligencia is history, never active work. Shared by
// causaDiligenciaStage (below), the dashboard model and the diligencia
// detail's stepper — one mapping, not two.
export function stageKeyForStage(stage) {
  const s = (stage || '').toUpperCase();
  return (
    {
      DRAFT: 'borrador',
      QUOTING: 'cotizando',
      PAYMENT_DUE: 'porpagar',
      PAYMENT_PROCESSING: 'porpagar',
      PAYMENT_REJECTED: 'porpagar',
      IN_PROGRESS: 'encurso',
      CLOSED: 'cerrada',
      CANCELLED: 'cerrada',
    }[s] || 'desconocido'
  );
}

/**
 * Maps a per-case diligencia onto the stagepill.
 *
 * @param {{ stage, deadline, quotesSent, quotesOffered, dateOffered } | null} dilig
 * @returns {{ stage, tone, label, statusText, urgent }} where
 *   stage      — STAGE_DEFS key (or null when there's no active diligencia)
 *   tone       — stagepill--<tone> modifier
 *   label      — the pill text (the stage label)
 *   statusText — the secondary line under the pill (estado + offers tally)
 *   urgent     — drives the row's red <data-urgent> accent
 */
export function causaDiligenciaStage(dilig, now = Date.now()) {
  if (!dilig) {
    return {
      stage: null,
      tone: 'mute',
      label: 'Sin diligencia',
      statusText: '',
      urgent: false,
      attn: false,
    };
  }

  // The stage reading (label/sub/dot/tone/expired) comes from the SOT — never
  // re-derive the business rules here, just adapt their output. `dateOffered`
  // is forwarded because it CHANGES the reading (passed committed date →
  // "Atrasada") and feeds attn. A caller that omits it keeps the plain
  // reading.
  const display = diligenciaDisplay(dilig.stage, dilig.deadline, now, {
    deadlineExpiresAt: dilig.deadlineExpiresAt,
    dateOffered: dilig.dateOffered ?? null,
  });
  const deadline = formatDeadline(dilig.deadline, now, dilig.deadlineExpiresAt);

  // An IN_PROGRESS past its plazo reads 'Finalizada' (cierre natural) → the
  // terminal 'cerrada' bucket, even though the stored stage may still say
  // IN_PROGRESS. A pre-commitment diligencia past its plazo (display.expired)
  // reads 'Expirada' → also the terminal 'cerrada' bucket, so it leaves the
  // active columns instead of lingering in Borrador/Cotizando/Por pagar.
  const base = stageKeyForStage(dilig.stage);
  const stage =
    display.expired || display.label === 'Finalizada' ? 'cerrada' : base;

  // `status` is the plain estado line ("{d.id} · {status}"). `statusText`
  // appends the offers tally, but ONLY while quoting / pending pay — the Causas
  // column uses the plain one.
  const offers = dilig.quotesOffered || 0;
  const offersExtra =
    (stage === 'cotizando' || stage === 'porpagar') && offers > 0
      ? ` · ${offers} ${offers === 1 ? 'oferta' : 'ofertas'}`
      : '';
  const status = display.label;
  const statusText = `${status}${offersExtra}`;

  // tone: the bucket's own color (borrador=mute, cotizando=info,
  // porpagar=ready, en curso=live, cerrada=ok) — EXCEPT the derived Atrasada
  // (IN_PROGRESS with the committed date passed and the plazo valid), which
  // paints the pill warn (the intermediate state is distinguishable at a
  // glance). Expiry stays off the pill (a red pill would turn a whole column
  // red when many causas are overdue); it surfaces through the statusText and
  // the row's red accent.
  const tone =
    stage === 'encurso' && display.tone === 'warn' ? 'warn' : STAGE_TONE[stage];

  // urgent: the diligencia needs action AND the plazo is about to lapse.
  // "Needs action" = a late state or sitting in the pay queue — never a
  // terminal row: expirada/finalizada/cancelada are history, not work (same
  // rule as the Inicio foco and the Diligencias "Requiere acción" filter).
  // Note the gate is the bucket, not display.expired: EXPIRED also carries
  // row/tone 'err', so the err clause alone would keep it actionable. "About
  // to lapse" = formatDeadline's 'tight' band (≤ 2 días or past).
  const needsAction =
    stage !== 'cerrada' &&
    (display.row === 'err' || display.tone === 'warn' || stage === 'porpagar');
  const urgent = needsAction && deadline.urgency === 'tight';

  const def = STAGE_BY_KEY[stage] || {};
  return {
    stage,
    tone,
    // Terminal bucket: say HOW it ended (Finalizada/Cancelada/Expirada), not
    // the generic bucket name. The pay bucket keeps the generic "Por pagar";
    // WHICH pay state it is (Listo/Procesando/Reintentar) reads on the estado
    // line.
    label:
      (stage === 'cerrada' ? display.label : def.label) ||
      def.label ||
      display.label,
    status,
    statusText,
    urgent,
    // attn: needs operator action now (broader than urgent, which also requires
    // a tight plazo) — drives the "N requieren acción" headline count.
    attn: needsAction,
  };
}

/**
 * Maps a GET /tasks row (the active lane) onto the stagepill.
 *
 * The lane row carries `dateOffered` (the paid quote's committed date), which
 * changes the reading and therefore the attn flag, so the field set lives here
 * instead of at each call site: a page that rebuilds the shape by hand and
 * forgets it silently shrinks its "Requiere acción" set below the Inicio KPI
 * that counts the same rows.
 *
 * @param {object | null} task raw /tasks row
 * @returns {ReturnType<typeof causaDiligenciaStage>}
 */
export function taskDiligenciaStage(task, now = Date.now()) {
  if (!task) return causaDiligenciaStage(null);
  return causaDiligenciaStage(
    {
      stage: task.stage,
      deadline: task.deadline,
      deadlineExpiresAt: task.deadlineExpiresAt,
      quotesSent: task.quotesSent,
      quotesOffered: task.quotesOffered,
      dateOffered: task.dateOffered,
    },
    now,
  );
}
